'use strict';

const DIALOG_REGISTRY = require('../../dialogs/dialog.registry.js');

/**
 * Directive to display account-assignment autocomplete selection inputs.
 * Which account-assignment to display is determined via type.
 */
module.exports = (app) => app.directive('accountAssignments',  /* @ngInject */ function () {
    return {
        restrict: 'E',
        scope: {
            /**
             * REQUIRED
             * Pass the account-assignment-type:
             * Possible values:
             * -    ACCOUNT
             * -    COST_CENTER
             * -    PROJECT
             * -    CUSTOM
             *
             * When 'CUSTOM' is passed here, all custom account-assignment types will be displayed when they are
             * enabled for usage within the order process!
             */
            type: '=',

            /**
             * OPTIONAL
             * If a value shall be preselected, the account-assignment key can be passed here. The specific option will
             * be determined during the init process.
             */
            initValue: '=',

            /**
             * REQUIRED
             * When the content of the autocomplete needs to be submitted via form, the input id can be set here.
             */
            inputId: '@',

            /**
             * The subject the directive is placed in. Used between inner-subject communication between multiple account-assignment directived
             */
            subject: '@',

            /**
             * OPTIONAL
             * When inputIdPostfix has been set, the type.id that gets appended on the form variable name gets replaced by this value
             */
            inputIdPostfix: '@',

            mandatory: '=',

            /**
             * optional customer company id to fetch account assignments for a specific customer company
             * (only applicable for ADMIN users)
             */
            customerCompanyId: '@',

            /**
             * OPTIONAL
             * Generates a label based on the provided input.
             *
             * @param {boolean} showLabel - Indicates whether the label should be shown.
             * @returns {string} - The generated label.
             */
            showLabel: '=?',


            /**
             * @description If the input is readonly and not editable.
             *
             * @typedef {boolean} if true, the input is readonly.
             */
            readonly: '=?',

            labelClass: '@',

            inputClass: '@',
            /**
             * Set basket item id. When defined it will be used as additional identifier
             */
            basketItemId: '=',

            orderNumber: '@',

            rfqNumber: '@',
        },
        templateUrl: `./${BUILD_PATH}//account-assignment.directive.html`,
        controller: /* @ngInject */ function ($scope, accountAssignmentService, dialogService, translationService, userService, orderService, rfqService) {

            $scope.child = {}

            // init the property where childscopes will be stored in. Every child directive exposes their scope here.
            $scope.accountAssignmentAutocompleteScope = {};

            $scope.accountAssignments = null;
            $scope.customAccountAssignments = [];

            $scope.customAccountAssignmentSelected = {};
            $scope.baseAccountAssignmentSelected = '';

            $scope.customAccountAssignmentValues = {};

            $scope.onCustomAccountAssignmentChange = onCustomAccountAssignmentChange;
            $scope.onBaseAccountAssignmentChange = onBaseAccountAssignmentChange;

            $scope.value = null;

            $scope.editBase = true;
            $scope.setBaseEdit = setBaseEdit;

            $scope.editCustom = new Set();
            $scope.setCustomEdit = setCustomEdit;
            $scope.openDialog = openDialog;

            $scope.disabledIncompatibleType = null;
            $scope.incompatibleCustomAccountAssignmentTypeTranslations = null;
            /**
             * Compatible account-assignment types.
             * @type {{ACCOUNT: string, PROJECT: string, CUSTOM: string, COST_CENTER: string}}
             */
            const ACCOUNT_ASSIGNMENT_TYPES = accountAssignmentService.ACCOUNT_ASSIGNMENT_TYPES;
            const ACCOUNT_ASSIGNMENT_LABEL = {
                [ACCOUNT_ASSIGNMENT_TYPES.ACCOUNT]: translationService.translate(_('Sachkonto')),
                [ACCOUNT_ASSIGNMENT_TYPES.PROJECT]: translationService.translate(_('Projekt')),
                [ACCOUNT_ASSIGNMENT_TYPES.COST_CENTER]: translationService.translate(_('Kostenstelle')),
            };

            const DEFAULT_GRID_CLASS = 'col-12';

            $scope.labels = ACCOUNT_ASSIGNMENT_LABEL;
            $scope.types = ACCOUNT_ASSIGNMENT_TYPES

            let incompatibleCustomAccountAssignmentType = null;

            /**
             * Type of the currently logged in user:
             * "ADMIN", "SUPPLIER" or "CUSTOMER"
             */
            let userType;

            init();

            /**
             * Initiates the directive and fetches autocomplete options and account-assignment type information
             */
            async function init() {
                if (typeof $scope.showLabel === 'undefined') {
                    $scope.showLabel = true;
                }
                if (typeof $scope.readonly === 'undefined') {
                    $scope.readonly = false;
                }
                const myself = await userService.getMyself.get().$promise;
                userType = myself.type;
                if ($scope.type && ACCOUNT_ASSIGNMENT_TYPES.hasOwnProperty($scope.type)) {
                    $scope.type === ACCOUNT_ASSIGNMENT_TYPES.CUSTOM ? await getCustomAccountAssignments() : await getDefaultAccountAssignments($scope.type);
                }
                if (!$scope.labelClass) {
                    $scope.labelClass = DEFAULT_GRID_CLASS;
                }
                if (!$scope.inputClass) {
                    $scope.inputClass = DEFAULT_GRID_CLASS;
                }

                $scope.$apply();
            }

            function openDialog(title, accountAssignment, inputId) {
                if (!$scope.readonly) {
                    const dialogType = DIALOG_REGISTRY.ACCOUNT_ASSIGNMENT_DETAIL;

                    // dialog config
                    const config = {
                        title,
                        style: {
                            height: '50vh',
                            'max-height': '50vh',
                            width: '80vw',
                            'max-width': '80vw'
                        }
                    };

                    // determine entries
                    const entries = $scope.type === ACCOUNT_ASSIGNMENT_TYPES.CUSTOM ?
                        accountAssignment.entries :
                        accountAssignment;

                    // Pass both entries and type information to the dialog
                    const dialogData = {
                        entries: entries,
                        type: $scope.type,
                        // For custom account assignments, also pass the specific type ID
                        customTypeId: $scope.type === ACCOUNT_ASSIGNMENT_TYPES.CUSTOM ? accountAssignment.type.id : null,
                        // Pass through customerCompanyId when available
                        customerCompanyId: $scope.customerCompanyId || null
                    };

                    dialogService.open(dialogType.templatePath, dialogType.id, dialogData, config).then((selectedEntry) => {
                        // callback when the dialog has been closed. When an entry has been selected it will be passed down
                        // here and we need to update the autocomplete value
                        if (selectedEntry) {
                            if ($scope.type === ACCOUNT_ASSIGNMENT_TYPES.CUSTOM) {
                                $scope.customAccountAssignmentSelected[accountAssignment.type.id] = selectedEntry;
                            } else {
                                $scope.value = selectedEntry;
                                $scope.baseAccountAssignmentSelected = selectedEntry.code;
                            }

                            // set values in child directive manually because the change detection in this project is buggy and
                            // doesn't always detect the changed value. This is possible because the child directive exposes its
                            // scope to the parent
                            if ($scope.accountAssignmentAutocompleteScope[inputId]) {
                                $scope.accountAssignmentAutocompleteScope[inputId].value = selectedEntry;
                                $scope.accountAssignmentAutocompleteScope[inputId].init();
                            }
                            $scope.$apply();
                        }
                    });
                }
            }

            /**
             * A new item has been selected by the autocomplete directive for an custom account-assignment
             * @param event {
             *     old: {Object},
             *     new: {Object}
             * }
             */
            function onCustomAccountAssignmentChange(event) {
                if (event.new) {
                    $scope.customAccountAssignmentSelected[event.new.type] = event.new;
                    $scope.setCustomEdit(event.new.type, false);
                } else {
                    $scope.customAccountAssignmentSelected[event.old.type] = null;
                    $scope.setCustomEdit(event.old.type, true);
                }

                emitIncompatibleCustomAccountAssignmentChange(event.new ? event.new.type : event.old.type);
            }

            /**
             * A new item has been selected by the autocomplete directive for an base account-assignment
             }

             * @param event {
             *     old: {Object},
             *     new: {Object}
             * }
             */
            function onBaseAccountAssignmentChange(event) {
                if (event.new) {
                    $scope.baseAccountAssignmentSelected = event.new.code;
                    $scope.editBase = false;
                } else {
                    $scope.baseAccountAssignmentSelected = '';
                    $scope.editBase = true;
                }
                emitCostCenterChange()
            }

            /**
             * If 'ACCOUNT', 'COST_CENTER' or 'PROJECT' has been passed as type we can directly fetch the autocomplete options
             * @param type - {{ACCOUNT: string, PROJECT: string, CUSTOM: string, COST_CENTER: string}}
             */
            async function getDefaultAccountAssignments(type) {
                let searchFunction;

                switch (type) {
                    case ACCOUNT_ASSIGNMENT_TYPES.COST_CENTER:
                        listenToIncompatibleCustomAccountAssignmentEvents();
                        await fetchCostCenterSettings();
                        searchFunction = (query) => accountAssignmentService.searchCostCenters(query, getIncludeValue(), $scope.customerCompanyId);
                        break;
                    case ACCOUNT_ASSIGNMENT_TYPES.ACCOUNT:
                        searchFunction = (query) => accountAssignmentService.searchAccounts(query, getIncludeValue(), $scope.customerCompanyId);
                        break;
                    case ACCOUNT_ASSIGNMENT_TYPES.PROJECT:
                        searchFunction = (query) => accountAssignmentService.searchProjects(query, getIncludeValue(), $scope.customerCompanyId);
                        break;
                    default:
                        throw new Error('Unknown account-assignment type: ' + $scope.type);
                }

                if (searchFunction) {
                    $scope.searchFunction = searchFunction;

                    // Load initial data (first 50 entries)
                    try {
                        const initialData = await searchFunction('');
                        $scope.accountAssignments = initialData;
                        setInitialValue(initialData);
                        $scope.$apply();
                    } catch (e) {
                        console.error(e);
                        $scope.accountAssignments = [];
                        $scope.$apply();
                    }
                }
            }

            /**
             * Get include value for all account assignment types
             * @return {string|null}
             */
            function getIncludeValue() {
                // Return the currently selected base account assignment value if available
                if ($scope.baseAccountAssignmentSelected) {
                    return $scope.baseAccountAssignmentSelected;
                }
                return null;
            }

            function getCustomIncludeValue(typeId){
                // Return the currently selected custom account assignment value for the given type if available
                if ($scope.customAccountAssignmentSelected[typeId]) {
                    return $scope.customAccountAssignmentSelected[typeId].code;
                }
                return null;
            }

            /**
             * Determines the initial value based on the passed initValue account-assignment key.
             * @param options
             */
            function setInitialValue(options) {
                if ($scope.initValue) {
                    $scope.value = options.find(option => option.code === $scope.initValue);
                    $scope.baseAccountAssignmentSelected = $scope.initValue;
                    $scope.editBase = false;
                }
                emitCostCenterChange()
            }

            /**
             * Before fetching each custom account-assignment option we need to fetch all custom account-assignment types
             * enabled for usage within the order process. Based on those types we fetch the autocomplete options.
             * Only types with entries for the specified customer-company-id will be displayed.
             */
            async function getCustomAccountAssignments() {
                listenToCostCenterEvents();
                await fetchCostCenterSettings();
                const types = await accountAssignmentService.getCustomAccountAssignmentActiveInOrder($scope.orderNumber, $scope.rfqNumber, $scope.customerCompanyId)
                // types are defined and not empty
                if (types && Array.isArray(types) && types.length) {
                    for (let customAccountAssignmentType of types) {
                        const typeId = customAccountAssignmentType.id;
                        const initCode = getCustomAccountAssignmentInitCode(typeId);
                        $scope.customAccountAssignmentSelected[typeId] = '';

                        try {
                            // Create search function for this custom account assignment type
                            const searchFunction = (query, include = undefined) =>
                                accountAssignmentService.searchCustomAccountAssignments(typeId, query, include, $scope.customerCompanyId);

                            // Load initial entries (first 50) for the specified customer-company-id
                            const initialEntries = await searchFunction('', initCode ?? undefined);

                            // Only add types that have entries for this customer-company-id
                            // Mandatory types are only shown if they have entries for this customer
                            if (initialEntries && initialEntries.length > 0) {
                                // Add type property to each entry for compatibility
                                initialEntries.forEach(entry => entry.type = typeId);

                                $scope.customAccountAssignments.push({
                                    type: customAccountAssignmentType,
                                    entries: initialEntries,
                                    searchFunction: searchFunction
                                });
                            }
                        } catch (e) {
                            console.error(e);
                        }
                    }
                }
                setCustomAccountAssignmentInitValues();
                $scope.$apply();
            }

            function setBaseEdit(isEdit) {
                if (!$scope.readonly) {
                    if (isEdit) {
                        $scope.value = null;
                        $scope.baseAccountAssignmentSelected = '';
                    }

                    $scope.editBase = isEdit;
                    emitCostCenterChange()
                }
            }

            function setCustomEdit(typeId, isEdit) {
                if (!$scope.readonly) {
                    if (isEdit) {
                        $scope.editCustom.add(typeId);
                        $scope.customAccountAssignmentSelected[typeId] = null;
                    } else {
                        $scope.editCustom.delete(typeId);
                    }

                    emitIncompatibleCustomAccountAssignmentChange(typeId);
                }
            }

            function setCustomAccountAssignmentInitValues() {
                if ($scope.initValue && $scope.initValue !== '?') {
                    try {
                        for (const val of JSON.parse(`[${$scope.initValue}]`)) {
                            const accountAssignment = $scope.customAccountAssignments.find(type => type.type.id === +val.type);
                            if (accountAssignment) {
                                const entry = accountAssignment.entries.find(item => item.code === val.code);
                                if (entry) {
                                    $scope.customAccountAssignmentSelected[entry.type] = entry;
                                }
                            }
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }
                for (const type of $scope.customAccountAssignments) {
                    if (!$scope.customAccountAssignmentSelected[type.type.id]) {
                        $scope.editCustom.add(type.type.id);
                    }
                    emitIncompatibleCustomAccountAssignmentChange(type.type.id);
                }
                $scope.$apply();
            }

            function getCustomAccountAssignmentInitCode(typeId) {
                // Guard against undefined or placeholder init values which would make invalid JSON like "[undefined]"
                if (!$scope.initValue || $scope.initValue === '?') {
                    return undefined;
                }
                try {
                    for (const val of JSON.parse(`[${$scope.initValue}]`)) {
                        if (val.type === typeId) {
                            return val.code;
                        }
                    }
                } catch (e) {
                    console.error(e);
                }
                return undefined;
            }

            /**************************************************************************************************************
             * It is possible to set a custom account assignment that is incompatible to cost centers. That means if this
             * option has been made either cost center or the selected custom account assignment type needs to be empty.
             *
             * To enforce this we want to disable the autocomplete that is still empty if the other one has been set.
             *
             * Originally this directive is designed to display only the accounting type which has been provided via attribute.
             * All of a sudden communication between dropdowns is necessary. That communication is implemented via
             * custom events thrown by either one of the accounting types.
             *************************************************************************************************************/

            function emitCostCenterChange() {
                if ($scope.type === ACCOUNT_ASSIGNMENT_TYPES.COST_CENTER) {
                    const event = new CustomEvent(generateCostCenterEventId(), {detail: $scope.baseAccountAssignmentSelected});
                    window.dispatchEvent(event);
                }
            }

            function emitIncompatibleCustomAccountAssignmentChange(type) {
                if ($scope.type === ACCOUNT_ASSIGNMENT_TYPES.CUSTOM && type === incompatibleCustomAccountAssignmentType) {
                    const event = new CustomEvent(generateIncompatibleCustomAccountAssignmentEventId(), {detail: $scope.customAccountAssignmentSelected[type]});
                    window.dispatchEvent(event);
                }
            }

            function listenToCostCenterEvents() {
                window.addEventListener(generateCostCenterEventId(), onCostCenterChangeEvent)
            }

            function listenToIncompatibleCustomAccountAssignmentEvents() {
                if ($scope.type === ACCOUNT_ASSIGNMENT_TYPES.COST_CENTER) {
                    window.addEventListener(generateIncompatibleCustomAccountAssignmentEventId(), onIncompatibleCustomAccountAssignmentChangeEvent)
                }
            }

            function onIncompatibleCustomAccountAssignmentChangeEvent(event) {
                if (event && typeof event.detail !== "undefined") {
                    $scope.disabledIncompatibleType = event.detail;
                }
            }

            function onCostCenterChangeEvent(event) {
                if (event && typeof event.detail !== "undefined") {
                    $scope.disabledIncompatibleType = !!event.detail ? incompatibleCustomAccountAssignmentType : null;
                }
            }

            function generateCostCenterEventId() {
                return `${ACCOUNT_ASSIGNMENT_TYPES.COST_CENTER}_${$scope.subject}_change`;
            }

            function generateIncompatibleCustomAccountAssignmentEventId() {
                return `incompatible_${ACCOUNT_ASSIGNMENT_TYPES.CUSTOM}_${$scope.subject}_change`;
            }

            async function fetchCostCenterSettings() {
                let promise;
                const customerCompanyId = $scope.customerCompanyId;
                if (userType === userService.USER_TYPES.ADMIN && customerCompanyId) {
                    promise = accountAssignmentService.getCostCentersSettingsAdmin(customerCompanyId);
                } else if(userType === userService.USER_TYPES.ADMIN && customerCompanyId) {
                    console.error('To fetch cost center settings as ADMIN user the customerCompanyId needs to be set!');
                    return Promise.reject();
                } else if (userType === userService.USER_TYPES.CUSTOMER) {
                    promise = accountAssignmentService.getCostCentersSettings()
                }

                return new Promise(resolve => promise.then((settings) => {
                    if (settings && settings.incompatibleCustomAccountAssignmentId) {
                        incompatibleCustomAccountAssignmentType = settings.incompatibleCustomAccountAssignmentId;
                        accountAssignmentService.getCustomAccountAssignmentActiveInOrder($scope.orderNumber, $scope.rfqNumber, $scope.customerCompanyId).then((types) => {
                            $scope.incompatibleCustomAccountAssignmentTypeTranslations =
                                types.find(type => type.id === settings.incompatibleCustomAccountAssignmentId)?.translations;
                        })
                    }
                    resolve();
                }), () => resolve())
            }

            function removeListeners() {
                window.removeEventListener(generateIncompatibleCustomAccountAssignmentEventId(), onIncompatibleCustomAccountAssignmentChangeEvent);
                window.removeEventListener(generateCostCenterEventId(), onCostCenterChangeEvent);
            }

            $scope.$on('$destroy', removeListeners);
        }

    }
});
